import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const LoadingScreen = () => {
  const navigate = useNavigate();
  const [animations, setAnimations] = useState({
    spin: 0,
    fade: 0,
    scale: 0.8
  });
  
  const animationRef = useRef(null);
  const spinAnimationRef = useRef(null);
  const scaleAnimationRef = useRef(null);
  const fadeAnimationRef = useRef(null);

  const check = async () => {
    const userData = localStorage.getItem('userData');
    
    // Simuler un délai de chargement minimum
    setTimeout(() => {
      if (userData !== null && userData !== 'null' && userData !== 'undefined') {
        navigate('/menu');
      } else {
        navigate('/onboarding');
      }
    }, 2500); // 2.5 secondes pour montrer l'animation
  };

  useEffect(() => {
    check();
    
    // Animation de rotation
    let startTime = null;
    let animationFrameId = null;
    
    const animateSpin = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = (elapsed % 2000) / 2000; // 2 secondes pour un cycle complet
      
      setAnimations(prev => ({
        ...prev,
        spin: progress
      }));
      
      animationFrameId = requestAnimationFrame(animateSpin);
    };
    
    spinAnimationRef.current = requestAnimationFrame(animateSpin);
    
    // Animation de fondu
    let fadeDirection = 1;
    let fadeValue = 0;
    let fadeStartTime = null;
    
    const animateFade = (timestamp) => {
      if (!fadeStartTime) fadeStartTime = timestamp;
      const elapsed = timestamp - fadeStartTime;
      
      if (fadeDirection === 1) {
        // Fade in
        fadeValue = Math.min(1, elapsed / 1000); // 1 seconde pour fade in
        if (fadeValue >= 1) {
          fadeDirection = -1;
          fadeStartTime = timestamp;
        }
      } else {
        // Fade out
        fadeValue = Math.max(0.3, 1 - ((elapsed) / 1000)); // 1 seconde pour fade out
        if (fadeValue <= 0.3) {
          fadeDirection = 1;
          fadeStartTime = timestamp;
        }
      }
      
      setAnimations(prev => ({
        ...prev,
        fade: fadeValue
      }));
      
      fadeAnimationRef.current = requestAnimationFrame(animateFade);
    };
    
    fadeAnimationRef.current = requestAnimationFrame(animateFade);
    
    // Animation de scale
    let scaleDirection = 1;
    let scaleValue = 0.8;
    let scaleStartTime = null;
    
    const animateScale = (timestamp) => {
      if (!scaleStartTime) scaleStartTime = timestamp;
      const elapsed = timestamp - scaleStartTime;
      
      if (scaleDirection === 1) {
        // Scale up
        scaleValue = 0.8 + (0.2 * Math.min(1, elapsed / 800)); // 0.8s pour scale up
        if (scaleValue >= 1) {
          scaleDirection = -1;
          scaleStartTime = timestamp;
        }
      } else {
        // Scale down
        scaleValue = 1 - (0.2 * Math.min(1, elapsed / 800)); // 0.8s pour scale down
        if (scaleValue <= 0.8) {
          scaleDirection = 1;
          scaleStartTime = timestamp;
        }
      }
      
      setAnimations(prev => ({
        ...prev,
        scale: scaleValue
      }));
      
      scaleAnimationRef.current = requestAnimationFrame(animateScale);
    };
    
    scaleAnimationRef.current = requestAnimationFrame(animateScale);

    return () => {
      if (spinAnimationRef.current) {
        cancelAnimationFrame(spinAnimationRef.current);
      }
      if (fadeAnimationRef.current) {
        cancelAnimationFrame(fadeAnimationRef.current);
      }
      if (scaleAnimationRef.current) {
        cancelAnimationFrame(scaleAnimationRef.current);
      }
    };
  }, []);

  // Calculer la rotation en degrés
  const spinDegrees = animations.spin * 360;
  
  // Styles CSS
  const styles = {
    container: {
      display: 'flex',
      width: '100vw',
      height: '100vh',
      backgroundColor: '#ffffff',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
    },
    loaderContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      width: '200px',
      height: '200px',
    },
    outerRing: {
      width: '120px',
      height: '120px',
      borderRadius: '60px',
      border: '4px solid #3b82f6',
      borderTopColor: '#60a5fa',
      borderRightColor: '#93c5fd',
      borderBottomColor: '#bfdbfe',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: `translate(-50%, -50%) rotate(${spinDegrees}deg)`,
      opacity: animations.fade,
      transition: 'opacity 0.1s linear',
    },
    innerCircle: {
      width: '80px',
      height: '80px',
      borderRadius: '40px',
      backgroundColor: '#3b82f6',
      opacity: 0.2,
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: `translate(-50%, -50%) scale(${animations.scale})`,
      transition: 'transform 0.1s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    dot: {
      width: '20px',
      height: '20px',
      borderRadius: '10px',
      backgroundColor: '#3b82f6',
      position: 'absolute',
      top: '0',
      left: '50%',
      transform: `translateX(-50%) rotate(${spinDegrees}deg)`,
      transformOrigin: '50% 60px', // 60px = radius (60px) + dot offset (0px)
    },
    loadingText: {
      marginTop: '140px',
      fontSize: '18px',
      fontWeight: '600',
      color: '#1e293b',
      letterSpacing: '0.5px',
      textAlign: 'center',
    },
    subText: {
      marginTop: '8px',
      fontSize: '14px',
      color: '#64748b',
      fontWeight: '400',
      textAlign: 'center',
    },
    
    // Animation keyframes pour une approche alternative
    '@keyframes spin': {
      '0%': {
        transform: 'translate(-50%, -50%) rotate(0deg)',
      },
      '100%': {
        transform: 'translate(-50%, -50%) rotate(360deg)',
      },
    },
    '@keyframes fade': {
      '0%, 100%': {
        opacity: 0.3,
      },
      '50%': {
        opacity: 1,
      },
    },
    '@keyframes pulse': {
      '0%, 100%': {
        transform: 'translate(-50%, -50%) scale(0.8)',
      },
      '50%': {
        transform: 'translate(-50%, -50%) scale(1)',
      },
    },
  };

  // Version alternative avec CSS animations (plus simple)
  const alternativeStyles = {
    container: {
      display: 'flex',
      width: '100vw',
      height: '100vh',
      backgroundColor: '#ffffff',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
    },
    loaderContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      width: '200px',
      height: '200px',
    },
    outerRing: {
      width: '120px',
      height: '120px',
      borderRadius: '60px',
      border: '4px solid',
      borderColor: '#3b82f6',
      borderTopColor: '#60a5fa',
      borderRightColor: '#93c5fd',
      borderBottomColor: '#bfdbfe',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      animation: 'spin 2s linear infinite, fade 2s ease-in-out infinite',
    },
    innerCircle: {
      width: '80px',
      height: '80px',
      borderRadius: '40px',
      backgroundColor: '#3b82f6',
      opacity: 0.2,
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      animation: 'pulse 1.6s ease-in-out infinite',
    },
    dot: {
      width: '20px',
      height: '20px',
      borderRadius: '10px',
      backgroundColor: '#3b82f6',
      position: 'absolute',
      top: '0',
      left: '50%',
      transform: 'translateX(-50%)',
      animation: 'spin 2s linear infinite',
      transformOrigin: '50% 60px',
    },
    loadingText: {
      marginTop: '140px',
      fontSize: '18px',
      fontWeight: '600',
      color: '#1e293b',
      letterSpacing: '0.5px',
      textAlign: 'center',
    },
    subText: {
      marginTop: '8px',
      fontSize: '14px',
      color: '#64748b',
      fontWeight: '400',
      textAlign: 'center',
    },
  };

  // CSS animations keyframes
  const cssAnimations = `
    @keyframes spin {
      0% {
        transform: translate(-50%, -50%) rotate(0deg);
      }
      100% {
        transform: translate(-50%, -50%) rotate(360deg);
      }
    }
    
    @keyframes fade {
      0%, 100% {
        opacity: 0.3;
      }
      50% {
        opacity: 1;
      }
    }
    
    @keyframes pulse {
      0%, 100% {
        transform: translate(-50%, -50%) scale(0.8);
      }
      50% {
        transform: translate(-50%, -50%) scale(1);
      }
    }
  `;

  // Choisir quelle version utiliser
  const useCSSTransitions = true; // Mettre à false pour utiliser la version JavaScript

  return (
    <div style={useCSSTransitions ? alternativeStyles.container : styles.container}>
      <style>{cssAnimations}</style>
      
      <div style={useCSSTransitions ? alternativeStyles.loaderContainer : styles.loaderContainer}>
        {/* Anneau externe rotatif */}
        <div
          style={useCSSTransitions ? alternativeStyles.outerRing : styles.outerRing}
        />

        {/* Cercle central pulsant */}
        <div
          style={useCSSTransitions ? alternativeStyles.innerCircle : styles.innerCircle}
        />

        {/* Point rotatif */}
        <div
          style={useCSSTransitions ? alternativeStyles.dot : styles.dot}
        />

        <div style={useCSSTransitions ? alternativeStyles.loadingText : styles.loadingText}>
          Chargement...
        </div>
        <div style={useCSSTransitions ? alternativeStyles.subText : styles.subText}>
          Veuillez patienter
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;